import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaArrowRight } from "react-icons/fa";

import { db } from "../../firebase/firebase";

import "./Projects.css";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("All");

  // ==========================================
  // LOAD PROJECTS FROM FIREBASE
  // ==========================================

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError("");

        const projectsRef = collection(db, "projects");

        const projectsQuery = query(projectsRef, orderBy("createdAt", "desc"));

        const snapshot = await getDocs(projectsQuery);

        const projectData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setProjects(projectData);
        setFilteredProjects(projectData);

        console.log("Public projects:", projectData);
      } catch (error) {
        console.error("PUBLIC PROJECTS ERROR:", error);

        // Fallback if createdAt ordering causes an issue
        try {
          const snapshot = await getDocs(collection(db, "projects"));

          const projectData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          setProjects(projectData);
          setFilteredProjects(projectData);
        } catch (fallbackError) {
          console.error("PROJECT FALLBACK ERROR:", fallbackError);

          setError("Unable to load projects right now.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  // ==========================================
  // SEARCH + FILTER
  // ==========================================

  useEffect(() => {
    const searchText = search.toLowerCase().trim();

    const filtered = projects.filter((project) => {
      const matchesSearch =
        project.title?.toLowerCase().includes(searchText) ||
        project.location?.toLowerCase().includes(searchText) ||
        project.state?.toLowerCase().includes(searchText) ||
        project.sport?.toLowerCase().includes(searchText);

      const matchesSport =
        sportFilter === "All" || project.sport === sportFilter;

      return matchesSearch && matchesSport;
    });

    setFilteredProjects(filtered);
  }, [search, sportFilter, projects]);

  // ==========================================
  // LOADING
  // ==========================================

  if (loading) {
    return (
      <section className="public-projects-page">
        <div className="projects-loading">
          <div className="projects-spinner"></div>

          <h3>Loading Projects...</h3>

          <p>Please wait while we load our completed projects.</p>
        </div>
      </section>
    );
  }

  // ==========================================
  // ERROR
  // ==========================================

  if (error) {
    return (
      <section className="public-projects-page">
        <div className="projects-error">
          <h2>Projects Unavailable</h2>

          <p>{error}</p>
        </div>
      </section>
    );
  }

  // ==========================================
  // PAGE
  // ==========================================

  return (
    <section className="public-projects-page">
      {/* ========================================
          HERO
      ======================================== */}

      <div className="public-projects-hero">
        <div className="projects-hero-content">
          <span className="projects-eyebrow">OUR WORK</span>

          <h1>
            Completed Sports
            <span> Projects</span>
          </h1>

          <p>
            Explore our completed sports surface construction projects across
            India.
          </p>
        </div>
      </div>

      {/* ========================================
          PROJECT CONTENT
      ======================================== */}

      <div className="public-projects-container">
        {/* FILTER BAR */}

        <div className="public-projects-toolbar">
          <div className="projects-search-box">
            <input
              type="text"
              placeholder="Search projects, location or sport..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            value={sportFilter}
            onChange={(e) => setSportFilter(e.target.value)}
          >
            <option value="All">All Sports</option>

            <option value="Basketball">Basketball</option>

            <option value="Tennis">Tennis</option>

            <option value="Badminton">Badminton</option>

            <option value="Football">Football</option>

            <option value="Volleyball">Volleyball</option>

            <option value="Running Track">Running Track</option>

            <option value="Pickleball">Pickleball</option>

            <option value="Squash">Squash</option>

            <option value="Cricket">Cricket</option>
          </select>
        </div>

        {/* PROJECT COUNT */}

        <div className="public-project-count">
          Showing <strong>{filteredProjects.length}</strong>{" "}
          {filteredProjects.length === 1 ? "project" : "projects"}
        </div>

        {/* ======================================
            PROJECT GRID
        ====================================== */}

        {filteredProjects.length > 0 ? (
          <div className="public-projects-grid">
            {filteredProjects.map((project) => (
              <article className="public-project-card" key={project.id}>
                {/* IMAGE */}

                <div className="public-project-image">
                  {project.coverImage ? (
                    <img
                      src={project.coverImage}
                      alt={project.title || "Sports construction project"}
                    />
                  ) : (
                    <div className="project-no-image">No Image</div>
                  )}

                  {/* FEATURED */}

                  {project.featured && (
                    <span className="public-featured">Featured</span>
                  )}

                  {/* SPORT */}

                  {project.sport && (
                    <span className="public-project-sport">
                      {project.sport}
                    </span>
                  )}
                </div>

                {/* CONTENT */}

                <div className="public-project-content">
                  <h2>{project.title}</h2>

                  {(project.location || project.state) && (
                    <div className="public-project-location">
                      <FaMapMarkerAlt />

                      <span>
                        {project.location}

                        {project.location && project.state ? ", " : ""}

                        {project.state}
                      </span>
                    </div>
                  )}

                  {project.surface && (
                    <div className="public-project-surface">
                      Surface:
                      <strong>{project.surface}</strong>
                    </div>
                  )}

                  <p>
                    {project.shortDescription ||
                      project.description ||
                      "Sports surface construction project completed by Quality Sports Surface."}
                  </p>

                  {/* VIEW */}

                  <Link
                    to={`/projects/${project.slug}`}
                    className="view-project-link"
                  >
                    View Project
                    <FaArrowRight />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        ) : (
          /* ====================================
             EMPTY
          ==================================== */

          <div className="public-projects-empty">
            <h2>No Projects Found</h2>

            <p>Try changing your search or sport filter.</p>

            <button
              onClick={() => {
                setSearch("");
                setSportFilter("All");
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
